import {
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  afterNextRender,
  afterRenderEffect,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ChatbotService } from './chatbot.service';
import { ChatbotAppTreeComponent } from './chatbot-app-tree/chatbot-app-tree.component';
import { ChatbotInstructionsComponent } from './chatbot-train/chatbot-train.component';
import { ChatMessageAction } from './chatbot.types';
import { HelpService } from '../help/help.service';

/** Draggable ARSON assistant that keeps conversations clear of navigation chrome and screen guides. */
@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss',
  imports: [TranslateModule, ChatbotAppTreeComponent, ChatbotInstructionsComponent],
  host: {
    '[class.chatbot--help-open]': 'help.isOpen()',
    '(window:resize)': 'onSafeZoneChanged()',
  },
})
export class ChatbotComponent implements OnDestroy {
  private readonly service = inject(ChatbotService);
  protected readonly help = inject(HelpService);
  private readonly platformId = inject(PLATFORM_ID);
  /** Host element reference used for drag-to-reposition. */
  private readonly hostEl = inject(ElementRef<HTMLElement>);

  // ANCHOR: State signals

  protected readonly messages = this.service.messages;
  protected readonly isTyping = this.service.isTyping;
  protected readonly isOpen = this.service.panelOpen;
  protected readonly inputText = signal<string>('');
  /** Whether the separate full-screen concept map tree dialog is open. */
  protected readonly isTreeOpen = signal<boolean>(false);
  /** Whether the instructions reference panel is open. */
  protected readonly isInstructionsOpen = signal<boolean>(false);
  /** Font size steps for chat bubbles (px), stepped via the A- / A+ buttons. */
  protected readonly FONT_STEPS = [13, 15, 17, 20] as const;
  /** Index into FONT_STEPS that determines the active bubble font size. */
  protected readonly fontSizeIdx = signal<number>(0);
  /** Active bubble font size in px (bound to `--chatbot-bubble-font-size` on the message list). */
  protected readonly fontSize = computed<number>(() => this.FONT_STEPS[this.fontSizeIdx()]);
  /** True while the user is dragging the FAB or header to reposition the panel. */
  protected readonly isDragging = signal<boolean>(false);
  /** True when the avatar image fails to load — hides the broken image element. */
  protected readonly avatarLoadFailed = signal<boolean>(false);
  /** True when the current user has the ARSON profile — gates the whole widget. */
  protected readonly isArsonProfile = this.service.isArsonProfile;
  /** 'GasBot' or 'AquaBot' depending on the active client's gas flag. */
  protected readonly chatbotTitle = this.service.chatbotTitle;
  /** True when the current screen has a guide and its panel is not already showing it. */
  protected readonly guideAvailable = computed<boolean>(
    () => !!this.help.currentTopic() && !this.help.isOpen(),
  );
  // True when the input field has non-whitespace content.
  protected readonly canSend = computed<boolean>(() => this.inputText().trim().length > 0);
  private readonly messagesContainer =
    viewChild<ElementRef<HTMLElement>>('messagesContainer');
  private readonly inputField =
    viewChild<ElementRef<HTMLInputElement>>('chatInput');

  // ANCHOR: Effects

  /**
   * Scrolls the conversation to the latest message when messages arrive or typing state changes.
   * Runs only while the panel is open and in a browser.
   *
   * Why effect() and not computed() or linkedSignal():
   * `scrollTop = scrollHeight` is an imperative DOM write — not a derived value.
   * computed() and linkedSignal() produce reactive values only. effect() is the
   * correct bridge for non-reactive side effects on third-party DOM state.
   */
  private readonly autoScroll = effect(() => {
    this.messages();
    this.isTyping();
    const open = this.isOpen();

    if (!open) { return; }
    if (!isPlatformBrowser(this.platformId)) { return; }

    setTimeout(() => {
      const el = this.messagesContainer()?.nativeElement;
      if (el) { el.scrollTop = el.scrollHeight; }
    }, 0);
  });

  // ANCHOR: Safe-zone observer
  // Watches the sidebar wrapper for class changes (sidebar-hide ↔ sidebar-show).
  // When the sidebar expands or collapses the left boundary of the safe zone changes,
  // so we re-run applyPanelDirection() and clamp the FAB inside the new safe zone.

  private sidebarObserver: MutationObserver | null = null;
  private positionBeforeHelp: { left: number; bottom: number } | null = null;

  constructor() {
    afterRenderEffect(onCleanup => {
      const helpOpen = this.help.isOpen();
      untracked(() => {
        this.ensureFabPos();
        if (helpOpen && !this.positionBeforeHelp) {
          this.positionBeforeHelp = { left: this.fabLeft, bottom: this.fabBottom };
          const panel = document.querySelector(".help-panel");
          if (panel && window.innerWidth > 767) {
            this.fabLeft = panel.getBoundingClientRect().left
              - ChatbotComponent.PANEL_GAP - ChatbotComponent.FAB_PX;
          }
        } else if (!helpOpen && this.positionBeforeHelp) {
          this.fabLeft = this.positionBeforeHelp.left;
          this.fabBottom = this.positionBeforeHelp.bottom;
          this.positionBeforeHelp = null;
        }
        this.applyPanelDirection();
        const panel = helpOpen ? document.querySelector(".help-panel") : null;
        if (panel) {
          const observer = new ResizeObserver(() => this.onSafeZoneChanged());
          observer.observe(panel);
          onCleanup(() => observer.disconnect());
        }
      });
    });
    // Also covers opening from outside (help panel "ask the assistant"), which skips toggle().
    effect(() => {
      if (this.isOpen()) {
        untracked(() => this.applyPanelDirection());
      }
    });
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) { return; }
      const wrapperEl = document.querySelector(
        '.sidebar-container .sidebar-wrapper',
      ) as HTMLElement | null;
      if (!wrapperEl) { return; }
      this.sidebarObserver = new MutationObserver(() => {
        // Give the CSS animation a tick to apply before reading the new rect.
        setTimeout(() => { this.onSafeZoneChanged(); }, 120);
      });
      this.sidebarObserver.observe(wrapperEl, { attributes: true, attributeFilter: ['class'] });
    });
  }

  ngOnDestroy(): void {
    this.sidebarObserver?.disconnect();
    this.endDrag();
  }

  /** Re-clamps the widget after viewport, sidebar or guide geometry changes. */
  protected onSafeZoneChanged(): void {
    if (this.help.isOpen() && window.innerWidth <= 767) { return; }
    if (this.fabLeft < 0) {
      // FAB position not yet determined — just refresh direction from scratch.
      this.applyPanelDirection();
      return;
    }
    const zone = this.getSafeZone();
    const FAB  = ChatbotComponent.FAB_PX;
    if (this.help.isOpen() && window.innerWidth > 767) {
      this.fabLeft = zone.right - FAB;
    }
    // Clamp horizontal position: push FAB right if sidebar just expanded over it.
    if (this.fabLeft < zone.left) {
      this.fabLeft = zone.left;
    } else if (this.fabLeft > zone.right - FAB) {
      this.fabLeft = zone.right - FAB;
    }
    this.applyPanelDirection();
  }

  // ANCHOR: Handlers

  // Toggle the chat panel open/closed.
  // Computes the correct panel direction before opening so it always fits on-screen.
  protected toggle(): void {
    if (!this.isOpen() && isPlatformBrowser(this.platformId)) {
      this.applyPanelDirection();
    }
    this.isOpen.update(v => !v);

    if (this.isOpen() && isPlatformBrowser(this.platformId)) {
      // Defer focus until the conditional panel/input has been rendered.
      setTimeout(() => {
        this.inputField()?.nativeElement.focus();
      }, 0);
    }
  }

  // FAB click: only toggles if the pointer did NOT move past the drag threshold.
  protected onFabClick(): void {
    const wasDragging = this.dragMoved;
    this.dragMoved = false;
    if (wasDragging) { return; }
    this.toggle();
  }

  protected openGuide(): void {
    this.help.open();
  }

  protected send(): void {
    const text = this.inputText().trim();
    if (!text) { return; }
    this.service.sendMessage(text);
    this.inputText.set('');
  }

  // Opens the instructions reference panel (read-only list of supported intents).
  protected openInstructions(): void {
    if (this.isArsonProfile()) {
      this.isInstructionsOpen.set(true);
    }
  }

  protected openTree(): void {
    this.isTreeOpen.set(true);
  }

  protected closeTree(): void {
    this.isTreeOpen.set(false);
  }

  protected closeInstructionsDialog(): void {
    this.isInstructionsOpen.set(false);
  }

  // Closes the intents panel, populates the input with the clicked example and sends it immediately.
  protected onExampleClicked(example: string): void {
    if (!this.isArsonProfile()) { return; }
    this.isInstructionsOpen.set(false);
    this.inputText.set(example);
    this.send();
  }

  // Clears chat messages, restoring the welcome message.
  protected clearChat(): void {
    this.service.clearMessages();
  }

  // Decrements the font size one step (minimum 13px).
  protected decreaseFontSize(): void {
    this.fontSizeIdx.update(i => Math.max(0, i - 1));
  }

  // Increments the font size one step (maximum 20px).
  protected increaseFontSize(): void {
    this.fontSizeIdx.update(i => Math.min(this.FONT_STEPS.length - 1, i + 1));
  }

  // ANCHOR: Drag & panel direction ────────────────────────────────────────
  // The host is a flex container; FAB and panel stack along the column axis.
  // Direction + alignment adapt to which screen quadrant the FAB is in so the
  // panel always opens into available space. The CSS anchor (right/left × top/bottom)
  // also switches per mode so the FAB stays visually fixed when the panel toggles.

  /** Min px² of pointer movement before a pointerdown becomes a drag (not a click). */
  private static readonly DRAG_THRESHOLD_SQ = 25; // ≥5 px
  private static readonly FAB_PX            = 56;
  private static readonly PANEL_PREF_H      = 520;
  private static readonly PANEL_PREF_W      = 420;
  private static readonly PANEL_GAP         = 12;

  /** Current layout mode — one of four screen quadrants. */
  private panelMode: 'above-right' | 'above-left' | 'below-right' | 'below-left' = 'above-right';

  /**
   * FAB position in viewport coordinates.
   * fabLeft   = FAB left edge from viewport left.
   * fabBottom = FAB bottom edge distance from viewport bottom.
   * −10 = not yet read from DOM (lazy-initialised on first open/drag).
   */
  private fabLeft   = -10;
  private fabBottom = -10;

  private dragActive        = false;
  private dragMoved         = false;
  private dragStartX        = 0;
  private dragStartY        = 0;
  private dragInitFabLeft   = 0;
  private dragInitFabBottom = 0;

  private readonly onDocMove = (e: PointerEvent): void => { this.applyDragMove(e); };
  private readonly onDocUp   = (): void => { this.endDrag(); };

  /** Begins tracking a drag from the FAB. Left button only. */
  protected onFabPointerDown(event: PointerEvent): void {
    if (!isPlatformBrowser(this.platformId) || event.button !== 0) { return; }
    this.prepareDrag(event);
  }

  /** Begins tracking a drag from the panel header; skips buttons and links. */
  protected onHeaderPointerDown(event: PointerEvent): void {
    if (!isPlatformBrowser(this.platformId) || event.button !== 0) { return; }
    if ((event.target as HTMLElement).closest('button, a')) { return; }
    event.preventDefault();
    this.prepareDrag(event);
  }

  private prepareDrag(event: PointerEvent): void {
    this.ensureFabPos();
    this.dragStartX        = event.clientX;
    this.dragStartY        = event.clientY;
    this.dragInitFabLeft   = this.fabLeft;
    this.dragInitFabBottom = this.fabBottom;
    this.dragMoved  = false;
    this.dragActive = true;
    document.addEventListener('pointermove',   this.onDocMove);
    document.addEventListener('pointerup',     this.onDocUp);
    document.addEventListener('pointercancel', this.onDocUp);
  }

  private applyDragMove(event: PointerEvent): void {
    if (!this.dragActive) { return; }
    const dx = event.clientX - this.dragStartX;
    const dy = event.clientY - this.dragStartY;
    if (dx * dx + dy * dy < ChatbotComponent.DRAG_THRESHOLD_SQ) { return; }

    if (!this.dragMoved) {
      this.dragMoved = true;
      this.isDragging.set(true);
      document.body.style.cursor     = 'grabbing';
      document.body.style.userSelect = 'none';
    }

    const zone = this.getSafeZone();
    const FAB  = ChatbotComponent.FAB_PX;
    // dy < 0 = pointer moved up = FAB bottom distance from viewport bottom increases.
    let newLeft   = this.dragInitFabLeft   + dx;
    let newBottom = this.dragInitFabBottom - dy;

    // Clamp FAB inside the safe zone.
    newLeft   = Math.max(zone.left,
                  Math.min(newLeft,   zone.right  - FAB));
    newBottom = Math.max(window.innerHeight - zone.bottom,
                  Math.min(newBottom, window.innerHeight - zone.top - FAB));

    this.fabLeft   = newLeft;
    this.fabBottom = newBottom;
    this.applyPanelDirection();
  }

  private endDrag(): void {
    this.dragActive = false;
    if (this.isDragging()) {
      this.isDragging.set(false);
      document.body.style.cursor     = '';
      document.body.style.userSelect = '';
      this.applyPanelDirection();
    }
    document.removeEventListener('pointermove',   this.onDocMove);
    document.removeEventListener('pointerup',     this.onDocUp);
    document.removeEventListener('pointercancel', this.onDocUp);
  }

  /**
   * Reads the FAB's screen position from the host bounding rect using the current
   * panelMode to identify which corner of the rect corresponds to the FAB.
   * Only called once (when fabLeft is the sentinel −10).
   */
  private ensureFabPos(): void {
    if (this.fabLeft >= 0) { return; }
    const rect = this.hostEl.nativeElement.getBoundingClientRect();
    const FAB  = ChatbotComponent.FAB_PX;
    if (this.help.isOpen() && window.innerWidth <= 767) {
      // Mobile help hides the host, so its zero-size rect cannot seed the saved position.
      const style = getComputedStyle(this.hostEl.nativeElement);
      this.fabLeft = window.innerWidth - parseFloat(style.right) - FAB;
      this.fabBottom = parseFloat(style.bottom);
      return;
    }
    // Horizontal: flex-end modes anchor to host right edge = FAB right.
    if (this.panelMode === 'above-right' || this.panelMode === 'below-right') {
      this.fabLeft = rect.right - FAB;
    } else {
      this.fabLeft = rect.left;
    }
    // Vertical: column-reverse (above) → host bottom = FAB bottom.
    if (this.panelMode === 'above-right' || this.panelMode === 'above-left') {
      this.fabBottom = window.innerHeight - rect.bottom;
    } else {
      this.fabBottom = window.innerHeight - rect.top - FAB;
    }
  }

  /**
   * Determines the correct panel mode for the FAB's current screen quadrant
   * (relative to the safe zone) then applies CSS to the host element:
   *   • Switches flex-direction / align-items via CSS custom properties.
   *   • Switches the CSS position anchor (right/left × top/bottom) so the FAB
   *     stays visually fixed as the panel opens or closes.
   *   • Constrains panel max-height and max-width to actual available space.
   */
  private applyPanelDirection(): void {
    if (!isPlatformBrowser(this.platformId)) { return; }
    if (this.help.isOpen() && window.innerWidth <= 767) { return; }
    this.ensureFabPos();

    const zone = this.getSafeZone();
    const FAB  = ChatbotComponent.FAB_PX;
    const GAP  = ChatbotComponent.PANEL_GAP;
    this.fabLeft = Math.max(zone.left, Math.min(this.fabLeft, zone.right - FAB));
    this.fabBottom = Math.max(window.innerHeight - zone.bottom,
      Math.min(this.fabBottom, window.innerHeight - zone.top - FAB));

    // FAB geometry in viewport coordinates (Y measured from top).
    const fabTopPx    = window.innerHeight - this.fabBottom - FAB;
    const fabBottomPx = window.innerHeight - this.fabBottom;
    const fabRightPx  = this.fabLeft + FAB;
    const fabCX       = this.fabLeft + FAB / 2;
    const fabCY       = fabTopPx     + FAB / 2;

    // Is the FAB centre in the lower / right half of the safe zone?
    const above      = fabCY > (zone.top  + zone.bottom) / 2;
    const rightAlign = this.help.isOpen() || fabCX > (zone.left + zone.right) / 2;

    if      ( above &&  rightAlign) { this.panelMode = 'above-right'; }
    else if ( above && !rightAlign) { this.panelMode = 'above-left';  }
    else if (!above &&  rightAlign) { this.panelMode = 'below-right'; }
    else                            { this.panelMode = 'below-left';  }

    // Available height in the chosen vertical direction.
    const availH = above
      ? fabTopPx    - zone.top    - GAP   // space above FAB
      : zone.bottom - fabBottomPx - GAP;  // space below FAB
    const panelMaxH = Math.max(0, Math.min(ChatbotComponent.PANEL_PREF_H, availH));

    // Available width in the chosen horizontal direction.
    const availW = rightAlign
      ? fabRightPx   - zone.left    // panel grows leftward from FAB right
      : zone.right   - this.fabLeft; // panel grows rightward from FAB left
    const panelMaxW = Math.max(0, Math.min(ChatbotComponent.PANEL_PREF_W, availW));

    // Apply the CSS anchor so the FAB stays in place when the panel opens/closes.
    // Horizontal: 'right' → host.right = FAB.right; 'left' → host.left = FAB.left.
    // Vertical:   'bottom' → host.bottom = FAB.bottom; 'top' → host.top = FAB.top.
    const el = this.hostEl.nativeElement;
    el.style.right  = rightAlign ? `${window.innerWidth - fabRightPx}px` : 'auto';
    el.style.left   = rightAlign ? 'auto' : `${this.fabLeft}px`;
    el.style.bottom = above      ? `${this.fabBottom}px` : 'auto';
    el.style.top    = above      ? 'auto' : `${fabTopPx}px`;

    // CSS custom properties consumed by :host in the SCSS.
    el.style.setProperty('--_flex-dir',    above ? 'column-reverse' : 'column');
    el.style.setProperty('--_flex-align',  rightAlign ? 'flex-end' : 'flex-start');
    el.style.setProperty('--_panel-max-h', `${panelMaxH}px`);
    el.style.setProperty('--_panel-max-w', `${panelMaxW}px`);
  }

  /**
   * Returns the usable viewport rectangle (px), excluding fixed chrome
   * (navbar-top, sidebar, footer). Falls back to conservative estimates when
   * elements are not found.
   *
   * For the sidebar, we read .sidebar-wrapper (the animated child) rather than
   * .sidebar-container (always 13 rem wide) so the left boundary correctly
   * reflects the collapsed (2.6 rem) vs expanded (13 rem) state,
   * because getBoundingClientRect() accounts for CSS transforms.
   */
  private getSafeZone(): { top: number; right: number; bottom: number; left: number } {
    const navbarEl  = document.querySelector('.header-container') as HTMLElement | null;
    const footerEl  = document.querySelector('.footer-container') as HTMLElement | null;
    // Use the inner wrapper, not the container — transform is reflected in its rect.
    const wrapperEl = document.querySelector(
      '.sidebar-container .sidebar-wrapper',
    ) as HTMLElement | null;
    return {
      top:    navbarEl  ? navbarEl.getBoundingClientRect().bottom  : 75,
      bottom: footerEl  ? footerEl.getBoundingClientRect().top     : window.innerHeight - 32,
      // Wrapper right edge = actual visible right edge of the sidebar.
      left:   wrapperEl ? Math.max(0, wrapperEl.getBoundingClientRect().right) : 42,
      right: this.help.isOpen()
        ? (document.querySelector(".help-panel")?.getBoundingClientRect().left ?? window.innerWidth)
          - ChatbotComponent.PANEL_GAP
        : window.innerWidth,
    };
  }

  // Navigates from the tree dialog and closes it.
  protected navigateFromTree(payload: string): void {
    this.service.navigateTo(payload);
    this.isTreeOpen.set(false);
  }

  // Enter sends unless Shift is held; Escape closes the panel.
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    } else if (event.key === 'Escape') {
      this.isOpen.set(false);
    }
  }

  protected getInputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  protected onMessageAction(action: ChatMessageAction): void {
    if (action.kind === 'open-help') {
      this.help.open(action.topicId, action.sectionId);
    }
  }
}